import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';

/** Viewport width below which the drawer opens as a modal overlay (no squeeze). */
const MODAL_BREAKPOINT = 990;

/** Original user-preferred slower spring tempo; native lifecycle and velocity continuity unchanged. */
const BAG_TIME_SCALE = 1.6;

/**
 * Cart-only assembly: header, products and checkout arrive on separate paths.
 * Each spring retains its presentation and velocity through interruptions.
 * Native dialog lifecycle and commerce remain owned by ThemeDrawer below.
 */
class BagAssembly {
  #panel;
  #shell = {position:0,velocity:0};
  #target = 0;
  #parts = [];
  #running = [];
  #resolve;
  #revision = 0;
  #fresh = false;
  #reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  constructor(panel) {
    this.#panel=panel;
    panel.setAttribute('data-sm-bag-motion','');
    this.#reduce.addEventListener('change',this.#onMotionPreference);
  }
  #onMotionPreference=()=>{if(this.#reduce.matches)this.finish(this.#target);};
  #clearPart(part) {
    for(const property of ['transform','opacity','will-change'])part.element.style.removeProperty(property);
    part.element.removeAttribute('data-sm-bag-part');
  }
  #collect(reset = false) {
    const previous = new Map(this.#parts.map(part => [part.element, part]));
    const parts = [];
    const add = (element, kind, x, y, delay = 0) => {
      if (!element) return;
      parts.push(previous.get(element) ?? {
        element, kind, x, y, delay, wait: 0, position: this.#shell.position, velocity: 0,
      });
      previous.delete(element);
    };
    add(this.#panel.querySelector('.theme-drawer__header'), 'header', 0, -32, 35);
    add(this.#panel.querySelector('.bb-cart-drawer__subhead'), 'estimate', 0, -24, 45);
    add(this.#panel.querySelector('.cart-drawer__summary'), 'checkout', 0, 64);
    add(this.#panel.querySelector('.bb-cart-drawer__empty-inner'), 'empty', 0, 32, 35);
    // Measure once at the transition boundary, never in the frame loop.
    const viewport = this.#panel.querySelector('.cart-drawer__items')?.getBoundingClientRect();
    const rows = [...this.#panel.querySelectorAll('.cart-items__table-row')]
      .filter(row => {
        if (!row.querySelector('.cart-items__title')) return false;
        const bounds = row.getBoundingClientRect();
        return bounds.height > 0 && (!viewport || bounds.bottom > viewport.top && bounds.top < viewport.bottom);
      }).slice(0, 4);
    rows.forEach((row, index) => add(row, 'item', 64, 0, 45 + index * 32));
    previous.forEach(part => this.#clearPart(part));
    this.#parts = parts;
    if (reset) this.#parts.forEach(part => { part.position = 100; part.velocity = 0; });
  }


  // Exact critically damped spring, sampled once into compositor keyframes.
  // No per-frame JavaScript, inherited variable changes, or DOM measurements.
  #sample(position,velocity,target,omega,seconds) {
    const displacement=position-target,coefficient=velocity+omega*displacement;
    const decay=Math.exp(-omega*seconds);
    return {position:target+(displacement+coefficient*seconds)*decay,
      velocity:(velocity-omega*coefficient*seconds)*decay};
  }
  #captureAndCancel() {
    ++this.#revision;
    for(const item of this.#running) {
      const ms=Math.max(0,Math.min(item.duration,Number(item.animation.currentTime)||0)-item.delay);
      Object.assign(item.state,this.#sample(item.position,item.velocity,item.target,item.omega,ms/1000));
      item.animation.cancel();
    }
    this.#running=[];
  }
  #paint() {
    const reveal=1-Math.max(0,Math.min(1,this.#shell.position/100));
    this.#panel.style.opacity=String(this.#reduce.matches?1:Math.min(1,reveal*4));
    for(const part of this.#parts) {
      const closed=Math.max(0,Math.min(1,part.position/100));
      part.element.style.transform=`translate3d(${part.x*closed}px,${part.y*closed}px,0)`;
      part.element.style.opacity=String(1-closed);
    }
  }
  prepare() {
    this.finish(100);this.#collect(true);this.#fresh=true;
    this.#panel.style.setProperty('--sm-bag-reveal','0');
    if(!this.#reduce.matches)this.#paint();
  }
  to(target) {
    this.#captureAndCancel();this.#resolve?.(false);this.#resolve=undefined;
    this.#target=target;this.#collect();
    const stagger=this.#fresh && target===0;this.#fresh=false;
    this.#panel.style.setProperty('--sm-bag-reveal',target===0?'1':'0');
    if(this.#reduce.matches){this.finish(target);return Promise.resolve(true);}
    const omega=(target===0?24:28)/BAG_TIME_SCALE,revision=this.#revision;
    const animate=(element,state,delay,shell=false)=>{
      const position=state.position,velocity=state.velocity,frames=[];
      let duration=0;
      for(let time=0;time<=2000;time+=12) {
        const sample=this.#sample(position,velocity,target,omega,time/1000);
        const closed=Math.max(0,Math.min(1,sample.position/100));
        frames.push(shell?{opacity:Math.min(1,(1-closed)*4)}:
          {transform:`translate3d(${state.x*closed}px,${state.y*closed}px,0)`,opacity:1-closed});
        duration=time;
        if(time>0 && Math.abs(sample.position-target)<.12 && Math.abs(sample.velocity)<5)break;
      }
      frames[frames.length-1]=shell?{opacity:target===0?1:0}:
        {transform:`translate3d(${target===0?0:state.x}px,${target===0?0:state.y}px,0)`,opacity:target===0?1:0};
      const animation=element.animate(frames,{duration,delay,easing:'linear',fill:'both'});
      this.#running.push({animation,state,position,velocity,target,omega,delay,duration:duration+delay});
    };
    this.#panel.style.willChange='opacity';animate(this.#panel,this.#shell,0,true);
    for(const part of this.#parts) {
      part.element.setAttribute('data-sm-bag-part',part.kind);
      part.element.style.willChange='transform, opacity';
      animate(part.element,part,stagger?part.delay*BAG_TIME_SCALE:0);
    }
    const finished=Promise.all(this.#running.map(item=>item.animation.finished.catch(()=>{})));
    return new Promise(resolve=>{
      this.#resolve=resolve;
      finished.then(()=>{if(revision===this.#revision)this.finish(target);});
    });
  }
  finish(target) {
    this.#captureAndCancel();this.#target=target;
    this.#shell.position=target;this.#shell.velocity=0;this.#fresh=false;
    this.#parts.forEach(part=>{part.position=target;part.velocity=0;});
    this.#paint();this.#parts.forEach(part=>this.#clearPart(part));
    this.#panel.style.setProperty('--sm-bag-reveal',target===0?'1':'0');
    this.#panel.style.removeProperty('will-change');
    const resolve=this.#resolve;this.#resolve=undefined;resolve?.(true);
  }
  destroy() {
    this.#captureAndCancel();this.#resolve?.(false);this.#resolve=undefined;
    this.#reduce.removeEventListener('change',this.#onMotionPreference);
    this.#parts.forEach(part=>this.#clearPart(part));this.#parts=[];
    this.#panel.removeAttribute('data-sm-bag-motion');
    for(const property of ['transform','opacity','will-change','--sm-bag-reveal'])this.#panel.style.removeProperty(property);
  }
}

/**
 * A drawer that opens from the right side.
 *
 * On wide viewports (≥ 990px) the drawer squeezes page content alongside it.
 * The panel is a non-modal dialog (`show()`); we install a focus trap via
 * `trapFocus()` so Tab cycles within the drawer, mirroring the modal-mode
 * a11y contract. Focus moves to the close button on open and returns to
 * the trigger on close.
 *
 * On narrow viewports (< 990px) the drawer overlays with a backdrop. The
 * panel is a modal dialog (`showModal()`) — native focus trap, scroll-lock,
 * and ARIA semantics. Same focus-on-close-button + restore-on-close UX.
 *
 * Dispatches {@link DrawerOpenEvent} and {@link DrawerCloseEvent}.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} panel - The dialog that owns all visual styles and animation.
 *
 * @extends {Component<Refs>}
 */
export class ThemeDrawer extends Component {
  requiredRefs = ['panel'];

  /** Monotonically increasing counter for z-index stacking of multiple open drawers. */
  static #stackOrder = 0;

  /** @type {boolean} */
  #isClosing = false;
  #restoreFocusOnClose = true;
  #motionRevision = 0;
  /** @type {BagAssembly | undefined} */
  #bagMotion;

  /** When true, the drawer will reopen after the current close animation finishes. */
  /** @type {{ enter?: 'inline-start' } | false} */
  #deferredOpen = false;

  /** Element to restore focus to when the drawer closes. */
  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /** @type {MediaQueryList} */
  #modalQuery = window.matchMedia(`(max-width: ${MODAL_BREAKPOINT - 1}px)`);

  /**
   * @returns {boolean} Whether the drawer is currently open.
   */
  get isOpen() {
    return this.hasAttribute('open');
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.id === 'cart-drawer') this.#bagMotion = new BagAssembly(this.refs.panel);
    this.#modalQuery.addEventListener('change', this.#onModalBreakpointChange);

    // Sync the static stack counter with any --drawer-stack-order set by the
    // synchronous restore script (which runs before this module loads).
    const restoredOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    if (restoredOrder > ThemeDrawer.#stackOrder) {
      ThemeDrawer.#stackOrder = restoredOrder;
    }

    if (this.isOpen) {
      this.#onRestore();
    }
  }

  /**
   * Restore path: the inline script in theme-drawer.liquid set [open] on
   * <theme-drawer> + <dialog> before this module loaded. The dialog is
   * already visible — we just wire close listeners. We deliberately skip
   * trapFocus and focus moves: the user is loading a fresh page and
   * expects focus on main content, not inside a drawer left over from
   * the previous session.
   */
  #onRestore() {
    const { panel } = this.refs;
    if (this.#modalQuery.matches) {
      lockScroll(panel);
    }

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    unlockScroll(this.refs.panel);
    this.#modalQuery.removeEventListener('change', this.#onModalBreakpointChange);
    this.#removeEventListeners();
    removeTrapFocus();
    this.#deferredOpen = false;
    ++this.#motionRevision;
    this.#bagMotion?.destroy();
  }

  /**
   * Closes the drawer when the user clicks the backdrop (outside the dialog
   * content area). Only meaningful in modal mode — in sidebar mode the
   * dialog has no backdrop so this handler is inert.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #onBackdropClick = (event) => {
    const { panel } = this.refs;

    if (isClickedOutside(event, panel)) {
      if (this.#hasOpenNestedDialog()) return;

      this.close();
    }
  };

  /**
   * @returns {boolean} Whether the drawer panel contains an open dialog other than itself.
   */
  #hasOpenNestedDialog() {
    return this.#getOpenNestedDialog() !== null;
  }

  /**
   * @returns {HTMLDialogElement | null} The open dialog nested inside the drawer panel, if any.
   */
  #getOpenNestedDialog() {
    return /** @type {HTMLDialogElement | null} */ (this.refs.panel.querySelector('dialog[open]'));
  }

  /**
   * Switches the dialog between modal and non-modal when the viewport
   * crosses the modal breakpoint while the drawer is open.
   */
  #onModalBreakpointChange = () => {
    if (!this.isOpen) return;

    const { panel } = this.refs;
    const nestedDialog = this.#getOpenNestedDialog();
    const nestedActiveElement =
      nestedDialog?.contains(document.activeElement) && document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    // Close the current dialog mode and immediately reopen in the new mode.
    // No animation — the drawer stays visually in place.
    panel.close();
    removeTrapFocus();

    if (this.#modalQuery.matches) {
      lockScroll(panel);
      panel.showModal();
    } else {
      unlockScroll(panel);
      panel.show();
      trapFocus(panel);
    }

    if (nestedDialog?.open) {
      this.#bringNestedDialogToFront(nestedDialog, nestedActiveElement);
    } else {
      this.#focusInitialElement();
    }

    // Ensure keydown and click listeners are registered. The sessionStorage
    // restore path bypasses open(), so these may not have been added yet.
    // addEventListener deduplicates identical listeners, so this is safe
    // even if they were already registered.
    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  };

  /**
   * Closes the drawer when the user presses Escape.
   * preventDefault() suppresses the native dialog cancel event in modal mode.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    // isComposing: don't close while an Input Method Editor (e.g. Japanese,
    // Chinese, Korean input) consumes Escape to dismiss its candidate list —
    // can happen inside the cart's discount-code field.
    // defaultPrevented: let nested components handle Escape first.
    if (event.isComposing || event.defaultPrevented) return;
    if (this.#bagMotion && this.#isClosing) {
      event.preventDefault();
      return;
    }
    if (!this.isOpen) return;
    if (!this.#isTopmost()) return;

    event.preventDefault();
    this.close();
  };

  /**
   * Shows the drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Slide direction. `'inline-start'` slides left-to-right (used when the chat docks).
   */
  open({ enter } = {}) {
    if (this.isOpen) {
      this.#bringToFront({ enter });
      return;
    }
    if (this.#isClosing) {
      if (this.#bagMotion) {
        // Reopen the same native dialog while its exit is still in flight.
        ++this.#motionRevision;
        this.#isClosing = false;
        this.#deferredOpen = false;
        this.refs.panel.classList.remove('theme-drawer__dialog--closing');
        this.setAttribute('open', '');
        this.#bagMotion.to(0);
        this.#focusInitialElement();
        this.dispatchEvent(new DrawerOpenEvent());
        return;
      }
      this.#deferredOpen = { enter };
      return;
    }

    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';
    panel.classList.add(openClass);

    this.#previouslyFocused = /** @type {HTMLElement | null} */ (document.activeElement);

    if (this.#modalQuery.matches) {
      lockScroll(panel);
      panel.showModal();
    } else {
      panel.show();
      trapFocus(panel);
    }

    this.#bagMotion?.prepare();
    this.#focusInitialElement();

    if (this.#bagMotion) {
      const revision = ++this.#motionRevision;
      this.#bagMotion.to(0).then((finished) => {
        if (finished && revision === this.#motionRevision) panel.classList.remove(openClass);
      });
    } else {
      onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });
    }

    this.setAttribute('open', '');
    this.dispatchEvent(new DrawerOpenEvent());

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  /**
   * Bumps this drawer's z-index to the top of the stack and replays
   * the slide-in animation. Called when open() is invoked on an
   * already-open drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Slide direction.
   */
  #bringToFront({ enter } = {}) {
    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    // In modal mode, dialogs live in the browser's top layer where z-index
    // is ignored — stacking follows showModal() call order. Re-calling
    // showModal() moves this dialog to the top of the stack.
    if (this.#modalQuery.matches && panel.open) {
      lockScroll(panel);
      panel.close();
      panel.showModal();
    }

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';
    if (!this.#bagMotion) {
      panel.classList.add(openClass);
      onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });
    }

    // Re-fire so persistDrawerState picks up the new stack order.
    this.dispatchEvent(new DrawerOpenEvent());
  }

  #removeEventListeners() {
    const { panel } = this.refs;

    document.removeEventListener('keydown', this.#onKeyDown);
    panel.removeEventListener('click', this.#onBackdropClick);
  }

  /**
   * Moves focus to the close button inside the drawer panel.
   * Every theme-drawer renders the close button via theme-drawer-header.
   * If a future drawer omits it, that drawer is responsible for its own focus management.
   */
  #focusInitialElement() {
    const closeButton = /** @type {HTMLElement | null} */ (
      this.refs.panel.querySelector('.theme-drawer__close-button')
    );
    closeButton?.focus();
  }

  /**
   * Reopens a nested modal after the drawer panel changes mode so it remains
   * above the drawer in the browser top layer.
   *
   * @param {HTMLDialogElement} dialog - The nested dialog to restack.
   * @param {HTMLElement | null} focusTarget - Element to refocus inside the nested dialog.
   */
  #bringNestedDialogToFront(dialog, focusTarget) {
    dialog.close();
    dialog.showModal();

    if (focusTarget && document.contains(focusTarget) && dialog.contains(focusTarget)) {
      focusTarget.focus();
    }
  }

  /**
   * Slides the drawer out, waits for the animation, then closes the dialog.
   */
  async #closeDialog({ immediate = false, restoreFocus = true } = {}) {
    if (!this.isOpen) return;
    if (this.#isClosing) return;
    this.#isClosing = true;
    this.#restoreFocusOnClose = restoreFocus;
    const revision = ++this.#motionRevision;

    if (!this.#bagMotion) {
      this.#removeEventListeners();
      removeTrapFocus();
    }

    const { panel } = this.refs;

    this.removeAttribute('open');
    this.dispatchEvent(new DrawerCloseEvent());

    if (!this.#bagMotion) unlockScroll(panel);

    if (panel.open) {
      // Cancel any in-progress open animation before starting the close.
      panel.classList.remove('theme-drawer__dialog--opening', 'theme-drawer__dialog--opening-inline-start');

      panel.classList.add('theme-drawer__dialog--closing');
      if (this.#bagMotion) {
        if (immediate) this.#bagMotion.finish(100);
        else await this.#bagMotion.to(100);
      } else if (!immediate) {
        await onAnimationEnd(panel, undefined, { subtree: false });
      }
      if (revision !== this.#motionRevision) return;
      panel.classList.remove('theme-drawer__dialog--closing');
    }

    if (this.#bagMotion) {
      this.#removeEventListeners();
      removeTrapFocus();
      unlockScroll(panel);
    }
    panel.close();
    this.style.removeProperty('--drawer-stack-order');

    if (!document.querySelector('theme-drawer[open]')) {
      ThemeDrawer.#stackOrder = 0;
    }

    const trigger = this.#previouslyFocused;
    this.#previouslyFocused = null;
    // A Section Rendering API morph between open and close can replace the
    // trigger node. The JS reference stays valid but the element is detached
    // from the live DOM — and .focus() on a detached node is a silent no-op,
    // so we explicitly check and fall back to a fresh query.
    if (!this.#restoreFocusOnClose) {
      // A different primary overlay owns focus now.
    } else if (trigger && document.contains(trigger)) {
      trigger.focus();
    } else {
      /** @type {HTMLElement | null} */ (document.querySelector(`[aria-controls="${this.id}"]`))?.focus();
    }

    this.#isClosing = false;

    // Reconcile: if open() was called during the close animation, reopen now.
    if (this.#deferredOpen) {
      const options = this.#deferredOpen;
      this.#deferredOpen = false;
      this.open(options);
    }
  }

  /**
   * Closes the drawer.
   */
  async close(options = {}) {
    if (this.#isClosing) {
      if (options.immediate) {
        this.#restoreFocusOnClose = options.restoreFocus !== false;
        if (this.#bagMotion) this.#bagMotion.finish(100);
        else this.refs.panel.getAnimations().forEach((animation) => animation.cancel());
        this.refs.panel.close();
      }
      // A close is already in progress — cancel any deferred open so the
      // drawer stays closed when the animation finishes.
      this.#deferredOpen = false;
      return;
    }
    await this.#closeDialog(options);
  }

  /**
   * Toggles the drawer. If the drawer is open but not the topmost in the
   * stack, it is brought to front instead of closed.
   */
  toggle() {
    if (this.isOpen && this.#isTopmost()) {
      return this.close();
    }
    return this.open();
  }

  /**
   * @returns {boolean} Whether this drawer has the highest stack order among all open drawers.
   */
  #isTopmost() {
    const myOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    for (const other of document.querySelectorAll('theme-drawer[open]')) {
      if (other === this) continue;
      const otherOrder = Number(/** @type {HTMLElement} */ (other).style.getPropertyValue('--drawer-stack-order') || 0);
      if (otherOrder > myOrder) return false;
    }
    return true;
  }
}

if (!customElements.get('theme-drawer')) {
  customElements.define('theme-drawer', ThemeDrawer);
}

export class DrawerOpenEvent extends CustomEvent {
  constructor() {
    super(DrawerOpenEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:open';
}

export class DrawerCloseEvent extends CustomEvent {
  constructor() {
    super(DrawerCloseEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:close';
}
